import { AjaxManager, useAjaxManager } from "@impero-com/common/utils/ajax";
import { RETRY_COUNT } from "@impero-com/common/utils/query_client";
import { isLoginSubdomain } from "@impero-com/common/utils/url";
import { useSuspenseQuery } from "@tanstack/react-query";

import { getUserStatus } from "api/generated/utils/utils";

import { useOrgDisplayInfo } from "../utils/queries/useOrgDisplayInfoSuspenseQuery";

import { AppAnonymous } from "./appAnonymous";
import { AppAuthenticated } from "./appAuthenticated";
import { AppDisabled } from "./appDisabled";
import { AppNoOrg } from "./appNoOrg";

function OrgAppSwitcher({ ajaxManager }: { ajaxManager: AjaxManager }) {
    const { isActive } = useOrgDisplayInfo();
    const { isLoggedIn } = useInitialLoginStatus();

    if (!isActive) {
        return <AppDisabled />;
    }

    if (isLoggedIn) {
        return <AppAuthenticated />;
    }

    return <AppAnonymous ajaxManager={ajaxManager} />;
}

export function AppSwitcher() {
    const ajaxManager = useAjaxManager();
    const isNoOrg = isLoginSubdomain();

    if (isNoOrg) {
        return <AppNoOrg ajaxManager={ajaxManager} />;
    }

    return <OrgAppSwitcher ajaxManager={ajaxManager} />;
}

const INITIAL_USER_STATUS_QUERY_KEY = "initial-user-status";
function useInitialLoginStatus() {
    const { data } = useSuspenseQuery({
        queryKey: [INITIAL_USER_STATUS_QUERY_KEY],
        queryFn: () =>
            getUserStatus().catch((err) => {
                console.error(
                    "Error while fetching initial user status: " + err.message
                );
                throw err;
            }),
        select: (response) => response.data,
        staleTime: Infinity,
        retry: RETRY_COUNT,
    });

    const isLoggedIn = data.session.type === "LoggedIn";
    return { isLoggedIn };
}
